/**
 * DescribeVariablesGrid widget — rendered inline in chat by the agent.
 *
 * Displays a compact table of described survey variables, each with an
 * individual "Add to Extraction" button. Rendered as the tool-specific
 * renderer for the `describe_survey_variables` (plural) Gateway tool.
 */

"use client"

import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Check, ChevronDown, ChevronRight, Plus, Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { addToExtraction, batchAddToExtraction } from "@/services/extractionsService"
import { buildExtractionKey, useExtractions } from "../ExtractionsContext"
import { getUnitOfAnalysisLabel } from "@/utils/surveyUtils"
import { DescribeVariableWidget } from "./DescribeVariableWidget"

/** A single value/description pair for a survey variable. */
interface VariableValue {
  value: string
  valueDescription: string
}

/** Shape of a single described variable as passed from the agent render tool. */
interface DescribedVariable {
  programName: string
  surveyId: string
  variableName: string
  variableLabel: string
  s3URI: string
  countryName: string
  surveyYear: string
  variableValues?: VariableValue[]
  /** MICS round identifier (only present for MICS surveys). */
  round?: string
  /** Unit of analysis code (recode for DHS/SPA, unitofanalysis for MICS). */
  unitOfAnalysis?: string
}

/** Shape of a failed variable entry returned by the Lambda. */
interface FailedVariable {
  programName: string
  surveyId: string
  variableName: string
  error: string
}

/**
 * Derive the source file name for a described variable from its s3URI.
 *
 * The extraction composite key uses the trailing path segment of the S3 URI
 * (e.g. "s3://bucket/.../KEHR7AFL.SAV" -> "KEHR7AFL.SAV").
 *
 * @param variable - The described variable whose file name is needed.
 * @returns The file name portion of the s3URI, or an empty string if absent.
 */
function deriveFileName({ variable }: { variable: DescribedVariable }): string {
  return variable.s3URI ? variable.s3URI.split("/").pop() ?? "" : ""
}

/**
 * Deduplicate a variable's value labels by value+description, dropping blanks.
 *
 * Non-categorical (numeric) variables yield an empty list, which the UI uses
 * as the signal that there are no discrete category values to show.
 *
 * @param variable - The described variable whose values should be deduped.
 * @returns A list of unique, non-empty value/description pairs.
 */
function dedupeVariableValues({ variable }: { variable: DescribedVariable }): VariableValue[] {
  return (
    variable.variableValues?.filter(
      (v, i, arr) =>
        v.value !== "" &&
        arr.findIndex(
          (x) => x.value === v.value && x.valueDescription === v.valueDescription
        ) === i
    ) ?? []
  )
}

/** Props for the DescribeVariablesGrid widget. */
interface DescribeVariablesGridProps {
  /** List of successfully described survey variables to display in the table. */
  variables: DescribedVariable[]
  /** List of variables that failed to describe (shown as error rows). */
  failedVariables?: FailedVariable[]
  /** The active chat session UUID, used when adding variables to the extraction. */
  sessionId: string
  /** Called when a variable is successfully added to the extraction. */
  onExtractionAdded?: () => void
}

/**
 * Renders a compact table of described survey variables. Each row shows the
 * variable name, label, survey ID, country, year, unit of analysis, an
 * expandable values sub-table, and an "Add" button for the extraction.
 * Failed variables are shown as error rows at the bottom.
 *
 * @param variables - Array of described variable objects from the agent.
 * @param failedVariables - Array of variables that failed to describe.
 * @param sessionId - The active chat session ID for extraction persistence.
 * @param onExtractionAdded - Callback fired after a successful extraction add.
 * @returns A Card containing a table of variable rows with action buttons.
 */
export function DescribeVariablesGrid({
  variables,
  failedVariables,
  sessionId,
  onExtractionAdded,
}: DescribeVariablesGridProps) {
  const auth = useAuth()
  const { addedKeys, refresh } = useExtractions()
  const [isAddingAll, setIsAddingAll] = useState<boolean>(false)
  const [addAllError, setAddAllError] = useState<string | null>(null)

  // Hooks must run unconditionally, so this early return comes after them.
  if (!variables || variables.length === 0) {
    return null
  }

  /** CSS Grid template: fixed columns + minmax for Label */
  const gridCols = "55px minmax(80px, 1fr) 85px 75px 45px 75px 50px 75px"

  // Variables not yet persisted to the extraction. "Add all" only acts on
  // these so re-clicking it after a partial add resumes where it left off.
  const notYetAdded: DescribedVariable[] = variables.filter(
    (variable) =>
      !addedKeys.has(
        buildExtractionKey({
          surveyId: variable.surveyId,
          fileName: deriveFileName({ variable }),
          variableName: variable.variableName,
        })
      )
  )

  // All displayed variables are already in the extraction — nothing left to add.
  const allAdded: boolean = notYetAdded.length === 0

  /**
   * Adds every not-yet-added variable in this grid to the session's extraction.
   *
   * Sends every not-yet-added variable to the batch endpoint in a single
   * request (same fileName derivation and value dedup as the per-row add),
   * then refreshes the shared extraction state once. The backend adds as many
   * as it can and skips any that already exist, so this never overwrites an
   * existing row. Fails loudly: a failed request surfaces the error rather
   * than being silently swallowed.
   *
   * @returns A Promise that resolves once the batch add completes.
   */
  async function handleAddAll(): Promise<void> {
    if (isAddingAll || notYetAdded.length === 0) {
      return
    }

    setIsAddingAll(true)
    setAddAllError(null)

    try {
      const accessToken = await auth.getAccessToken()
      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      // One request for the whole set — the backend batches the writes and
      // skips anything already present.
      await batchAddToExtraction(
        {
          sessionId,
          variables: notYetAdded.map((variable) => ({
            surveyId: variable.surveyId,
            variableName: variable.variableName,
            programName: variable.programName,
            variableLabel: variable.variableLabel,
            fileName: deriveFileName({ variable }),
            s3URI: variable.s3URI,
            values: dedupeVariableValues({ variable }),
            countryName: variable.countryName,
            surveyYear: variable.surveyYear,
            round: variable.round,
            unitOfAnalysis: variable.unitOfAnalysis,
          })),
        },
        accessToken
      )

      // Re-fetch once so every row's "already added" state updates together.
      await refresh()
      onExtractionAdded?.()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to add all variables to extraction"
      setAddAllError(message)
      console.error("Failed to add all variables to extraction:", err)
    } finally {
      setIsAddingAll(false)
    }
  }

  return (
    <Card className="my-2 border-primary/20 shadow-sm w-full py-2">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold">
              Described Variables
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {variables.length} variable{variables.length !== 1 ? "s" : ""} found
              {failedVariables && failedVariables.length > 0 && (
                <span className="text-destructive">
                  {" "}· {failedVariables.length} failed
                </span>
              )}
            </p>
          </div>
          <Button
            size="sm"
            variant={allAdded ? "outline" : "default"}
            className="h-7 gap-1 text-xs px-2 shrink-0 whitespace-nowrap"
            onClick={handleAddAll}
            disabled={isAddingAll || allAdded}
            aria-label={allAdded ? "All variables added to extraction" : "Add all variables to extraction"}
          >
            {/*
              Three visual states, matching the per-row button:
                1. Currently adding -> spinner
                2. All variables already added -> check icon + "All added"
                3. Some still pending -> plus icon + "Add all (N)"
            */}
            {isAddingAll ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : allAdded ? (
              <Check className="h-3 w-3" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
            {allAdded ? "All added" : `Add all (${notYetAdded.length})`}
          </Button>
        </div>
        {addAllError && (
          <p className="text-xs text-destructive mt-2">{addAllError}</p>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-0">
        <div className="rounded border overflow-x-auto text-xs">
          <div className="min-w-[630px]">
          {/* Header */}
          <div
            className="grid gap-x-2 px-3 py-2 border-b bg-muted/50 font-medium text-muted-foreground"
            style={{ gridTemplateColumns: gridCols }}
          >
            <div>Variable</div>
            <div>Label</div>
            <div>Survey</div>
            <div>Country</div>
            <div>Year</div>
            <div>Unit</div>
            <div>Category Values</div>
            <div></div>
          </div>
          {/* Rows */}
          {variables.map((variable, index) => (
            <VariableRow
              key={`${variable.surveyId}-${variable.variableName}-${index}`}
              variable={variable}
              sessionId={sessionId}
              onExtractionAdded={onExtractionAdded}
              gridCols={gridCols}
            />
          ))}
          {failedVariables && failedVariables.map((failed, index) => (
            <div
              key={`failed-${failed.surveyId}-${failed.variableName}-${index}`}
              className="grid gap-x-2 px-3 py-2 border-t"
              style={{ gridTemplateColumns: gridCols }}
            >
              <div className="font-mono text-destructive">{failed.variableName}</div>
              <div className="text-destructive col-span-6">{failed.error}</div>
              <div></div>
            </div>
          ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/** Props for a single variable row within the table. */
interface VariableRowProps {
  /** The described variable to render. */
  variable: DescribedVariable
  /** The active chat session UUID. */
  sessionId: string
  /** Called when this variable is successfully added to the extraction. */
  onExtractionAdded?: () => void
  /** CSS Grid column template shared with the parent. */
  gridCols: string
}

/**
 * A single table row within the DescribeVariablesGrid showing variable info,
 * an expandable values sub-row, and an add-to-extraction button.
 *
 * @param variable - The described variable data.
 * @param sessionId - The session UUID for extraction persistence.
 * @param onExtractionAdded - Callback fired on successful add.
 * @param gridCols - CSS Grid column template shared with the parent.
 * @returns One or two TableRow elements (main row + optional expanded values row).
 */
function VariableRow({ variable, sessionId, onExtractionAdded, gridCols }: VariableRowProps) {
  const [showValues, setShowValues] = useState<boolean>(false)
  const [isAdding, setIsAdding] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const auth = useAuth()
  const { addedKeys, refresh } = useExtractions()

  // Derive file name from s3URI
  const fileName: string = deriveFileName({ variable })

  // Check if already added to extraction
  const isAlreadyAdded: boolean = addedKeys.has(
    buildExtractionKey({
      surveyId: variable.surveyId,
      fileName: fileName,
      variableName: variable.variableName,
    })
  )

  // Deduplicate value labels by value+description.
  const uniqueValues: VariableValue[] = dedupeVariableValues({ variable })

  // Non-categorical (numeric) variables have no discrete value labels — an
  // empty uniqueValues list is the signal.
  const isCategorical: boolean = uniqueValues.length > 0

  /**
   * Handles adding this variable to the session's extraction.
   * Persists the variable to DynamoDB and refreshes the shared extraction state.
   *
   * @returns A Promise that resolves when the operation completes.
   */
  async function handleAddToExtraction(): Promise<void> {
    if (isAdding) {
      return
    }

    setIsAdding(true)
    setError(null)

    try {
      const accessToken = await auth.getAccessToken()
      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      await addToExtraction(
        {
          sessionId,
          surveyId: variable.surveyId,
          variableName: variable.variableName,
          programName: variable.programName,
          variableLabel: variable.variableLabel,
          fileName: fileName,
          s3URI: variable.s3URI,
          values: uniqueValues,
          countryName: variable.countryName,
          surveyYear: variable.surveyYear,
          round: variable.round,
          unitOfAnalysis: variable.unitOfAnalysis,
        },
        accessToken
      )

      await refresh()
      onExtractionAdded?.()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to add variable to extraction"
      setError(message)
      console.error("Failed to add variable to extraction:", err)
    } finally {
      setIsAdding(false)
    }
  }

  // Resolve the unit of analysis display label
  const unitLabel: string = variable.unitOfAnalysis
    ? getUnitOfAnalysisLabel(variable.unitOfAnalysis, variable.programName)
    : "—"

  return (
    <>
      <div
        className="grid gap-x-2 px-3 py-2 border-t items-center"
        style={{ gridTemplateColumns: gridCols }}
      >
        <div className="font-mono font-medium min-w-0 break-words">
          {variable.variableName}
        </div>
        <div className="break-words min-w-0">
          {variable.variableLabel}
        </div>
        <div className="min-w-0 break-words">{variable.surveyId}</div>
        <div className="min-w-0 break-words">{variable.countryName}</div>
        <div className="min-w-0 break-words">{variable.surveyYear}</div>
        <div className="min-w-0 break-words">{unitLabel}</div>
        <div className="whitespace-nowrap">
          {isCategorical ? (
            <button
              onClick={() => setShowValues(!showValues)}
              className="flex items-center gap-0.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showValues ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {uniqueValues.length}
            </button>
          ) : (
            // Non-categorical (numeric) variable: no value labels to expand.
            <span className="text-muted-foreground italic">none</span>
          )}
        </div>
        <div>
          <Button
            size="sm"
            variant={isAlreadyAdded ? "outline" : "default"}
            className="h-7 gap-1 text-xs px-2 whitespace-nowrap"
            onClick={handleAddToExtraction}
            disabled={isAdding || isAlreadyAdded}
          >
            {isAdding ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isAlreadyAdded ? (
              <Check className="h-3 w-3" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
            {isAlreadyAdded ? "Added" : "Add"}
          </Button>
          {error && <p className="text-xs text-destructive mt-0.5">{error}</p>}
        </div>
      </div>

      {showValues && (
        <div className="border-t py-1 px-4 bg-muted/30">
          <div className="max-h-40 overflow-y-auto rounded border bg-background">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 text-xs">Category</TableHead>
                  <TableHead className="text-xs">Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uniqueValues.map((v, i) => (
                  <TableRow key={`${v.value}-${i}`}>
                    <TableCell className="font-mono text-xs py-1">
                      {v.value}
                    </TableCell>
                    <TableCell className="text-xs py-1">
                      {v.valueDescription}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </>
  )
}


/** Props for the renderer wrapper used by ChatInterface's tool-specific renderer. */
interface DescribeVariablesRendererProps {
  /** Raw JSON string from the tool result. */
  result: string
  /** The active chat session UUID. */
  sessionId: string
  /** Called when a variable is successfully added to the extraction. */
  onExtractionAdded?: () => void
}

/**
 * Adaptive renderer for the `describe_survey_variables` tool result.
 *
 * Parses the JSON result and decides which UI to display:
 *   - 1 valid variable → renders the full DescribeVariableWidget card
 *   - 2+ valid variables → renders the compact DescribeVariablesGrid table
 *   - 0 valid variables or parse failure → returns null
 *
 * @param result - The raw JSON tool result string.
 * @param sessionId - The active session UUID.
 * @param onExtractionAdded - Callback for extraction refresh.
 * @returns The appropriate widget or null on parse failure.
 */
export function DescribeVariablesRenderer({
  result,
  sessionId,
  onExtractionAdded,
}: DescribeVariablesRendererProps) {
  try {
    const parsed = JSON.parse(result)
    const variables: (DescribedVariable & { error?: string })[] = parsed.surveyVariables ?? parsed.variables ?? []

    // Separate valid entries from failed entries
    const validVariables: DescribedVariable[] = variables.filter(
      (v): v is DescribedVariable => Boolean(v.variableName && v.variableLabel && v.s3URI && !("error" in v))
    )
    const failedVariables: FailedVariable[] = variables
      .filter((v) => "error" in v && v.error)
      .map((v) => ({
        programName: v.programName,
        surveyId: v.surveyId,
        variableName: v.variableName,
        error: v.error!,
      }))

    if (validVariables.length === 0 && failedVariables.length === 0) {
      return null
    }

    // Single valid variable with no failures — show the full card widget
    if (validVariables.length === 1 && failedVariables.length === 0) {
      const variable = validVariables[0]
      const singleResult = JSON.stringify({ surveyVariable: variable })
      return (
        <DescribeVariableWidget
          name="describe_survey_variables"
          args=""
          status="complete"
          result={singleResult}
          sessionId={sessionId}
          onExtractionAdded={onExtractionAdded}
        />
      )
    }

    // Multiple variables or any failures — show the compact grid table
    return (
      <DescribeVariablesGrid
        variables={validVariables}
        failedVariables={failedVariables.length > 0 ? failedVariables : undefined}
        sessionId={sessionId}
        onExtractionAdded={onExtractionAdded}
      />
    )
  } catch {
    return null
  }
}
